import { useEffect, useState, useCallback, useRef } from "react";
import { scrollStore } from "@/utils/scrollStore";
import Navbar from "@/components/landing/Navbar";
import EngineScene from "@/components/landing/EngineScene";
import ScrollOverlay from "@/components/landing/ScrollOverlay";
import LaunchCTA from "@/components/landing/LaunchCTA";
import LoadingScreen from "@/components/landing/LoadingScreen";
import WaitlistModal from "@/components/landing/WaitlistModal";
import Footer from "@/components/landing/Footer";

const BOOT_STAGES = [[0, 5], [200, 15], [500, 30], [900, 50], [1300, 70], [1700, 85]];
const MIN_DISPLAY_MS = 2400;

export default function LandingPage() {
  const [loading, setLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const sceneReady = useRef(false);
  const mountTime = useRef(Date.now());
  const igniteTimers = useRef([]);

  useEffect(() => {
    const timers = BOOT_STAGES.map(([delay, pct]) =>
      setTimeout(() => setLoadProgress((prev) => Math.max(prev, pct)), delay)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  const handleProgress = useCallback(() => {}, []);
  const handleReady = useCallback(() => {
    sceneReady.current = true;
    setLoadProgress(95);
    const elapsed = Date.now() - mountTime.current;
    const remaining = Math.max(0, MIN_DISPLAY_MS - elapsed);
    setTimeout(() => { setLoadProgress(100); setTimeout(() => setLoading(false), 500); }, remaining);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      scrollStore.progress = total > 0 ? window.scrollY / total : 0;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLaunch = useCallback(() => {
    setWaitlistOpen(true);
  }, []);

  const handleLaunchApp = useCallback(() => {
    if (scrollStore.igniting) return;
    setWaitlistOpen(false);
    // Trigger the in-scene "engine ignition" glow animation (not loading screen).
    scrollStore.igniting = true;
    igniteTimers.current.push(
      setTimeout(() => {
        scrollStore.igniting = false;
      }, 1000)
    );
    igniteTimers.current.push(
      setTimeout(() => {
        window.location.href = "https://early.qovarix.xyz";
      }, 900)
    );
  }, []);

  useEffect(() => {
    return () => {
      igniteTimers.current.forEach(clearTimeout);
      igniteTimers.current = [];
      scrollStore.igniting = false;
    };
  }, []);

  return (
    <div data-testid="qovarix-landing" className="relative" style={{ background: "#030406" }}>
      <LoadingScreen progress={loadProgress} visible={loading} />
      <div className="fixed inset-0 z-0 canvas-container">
        <EngineScene onProgress={handleProgress} onReady={handleReady} />
      </div>
      <Navbar onLaunch={handleLaunch} onLaunchApp={handleLaunchApp} />
      <div className="relative z-10">
        <ScrollOverlay onLaunch={handleLaunch} onLaunchApp={handleLaunchApp} />
        <LaunchCTA onLaunch={handleLaunch} onLaunchApp={handleLaunchApp} />
        <Footer />
      </div>
      <WaitlistModal open={waitlistOpen} onClose={() => setWaitlistOpen(false)} />
    </div>
  );
}
